import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ShieldAlert, Trash2, Loader2, Check, Sparkles, Target } from "lucide-react";

function formatDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";
  const [demoMode, setDemoMode] = useState(!!user?.demo_mode);
  const [demoSaving, setDemoSaving] = useState(false);
  const [demoMsg, setDemoMsg] = useState("");
  const [activation, setActivation] = useState(null);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationErr, setActivationErr] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    setActivationLoading(true);
    setActivationErr("");
    api
      .get("/admin/activation-stats")
      .then(({ data }) => setActivation(data))
      .catch((e) => setActivationErr(e.response?.data?.detail || "Could not load activation stats."))
      .finally(() => setActivationLoading(false));
  }, [isAdmin]);

  const toggleDemoMode = async () => {
    const next = !demoMode;
    setDemoSaving(true);
    setDemoMsg("");
    try {
      await api.patch("/users/me/settings", { demo_mode: next });
      setDemoMode(next);
      setDemoMsg(next ? "Demo mode enabled — AI-generated leads are now available." : "Demo mode disabled — only real sourced leads shown.");
      await refresh();
      setTimeout(() => setDemoMsg(""), 3500);
    } catch (e) {
      setDemoMsg("Could not update setting. Try again.");
      setTimeout(() => setDemoMsg(""), 3500);
    } finally {
      setDemoSaving(false);
    }
  };

  // Admin: wipe user state
  const [wipeEmail, setWipeEmail] = useState("");
  const [wiping, setWiping] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);
  const [wipeError, setWipeError] = useState("");

  // Admin: wipe all sample leads (global)
  const [wipingSamples, setWipingSamples] = useState(false);
  const [samplesResult, setSamplesResult] = useState(null);
  const [samplesError, setSamplesError] = useState("");

  const wipeSampleLeads = async () => {
    if (!window.confirm("Delete ALL sample (non-AI) leads across every user in the database? This cannot be undone.")) return;
    setWipingSamples(true);
    setSamplesResult(null);
    setSamplesError("");
    try {
      const { data } = await api.post("/admin/wipe-sample-leads");
      setSamplesResult(data);
    } catch (e) {
      setSamplesError(e.response?.data?.detail || "Wipe failed");
    } finally {
      setWipingSamples(false);
    }
  };

  const wipeUser = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Permanently delete ALL data (leads, campaigns, ICP, payments) for ${wipeEmail}? This cannot be undone.`)) return;
    setWiping(true);
    setWipeResult(null);
    setWipeError("");
    try {
      const { data } = await api.post("/admin/wipe-user", { email: wipeEmail });
      setWipeResult(data);
      setWipeEmail("");
    } catch (e) {
      setWipeError(e.response?.data?.detail || "Wipe failed");
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-3xl">
      <div className="mb-8">
        <div className="label-eyebrow mb-2">Workspace</div>
        <h1 className="font-display text-4xl font-medium text-white tracking-tight">Settings</h1>
        <p className="text-zinc-400 mt-2">Account and workspace preferences.</p>
      </div>

      <div className="glass-card p-6 mb-5" data-testid="settings-profile">
        <h2 className="font-display text-xl text-white mb-4">Profile</h2>
        <div className="space-y-4">
          <div>
            <div className="label-eyebrow mb-1.5">Name</div>
            <div className="input-glass">{user?.name}</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1.5">Email</div>
            <div className="input-glass">{user?.email}</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1.5">Plan</div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gold text-black" data-testid="plan-badge">
                Prospect · Trial
              </span>
              <span className="text-sm text-zinc-400">Contact us to discuss custom plans.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-6 mb-5" data-testid="settings-demo-mode">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h2 className="font-display text-xl text-white mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold" /> Demo mode
            </h2>
            <p className="text-sm text-zinc-400">
              When ON, the "Generate demo leads" button (AI-fabricated sample records) is available on the Leads page and background AI generation runs on ICP save. When OFF, only real ProPublica-sourced leads are surfaced.
            </p>
          </div>
          <button
            onClick={toggleDemoMode}
            disabled={demoSaving}
            className={`relative flex-shrink-0 w-14 h-8 rounded-full transition-colors border ${
              demoMode ? "bg-gold/80 border-gold" : "bg-white/5 border-white/10"
            }`}
            aria-label="Toggle demo mode"
            data-testid="demo-mode-toggle"
          >
            <span
              className={`absolute top-1 left-1 w-6 h-6 rounded-full transition-transform ${
                demoMode ? "translate-x-6 bg-black" : "bg-zinc-300"
              }`}
            />
          </button>
        </div>
        {demoMsg && (
          <div className="mt-2 text-xs text-zinc-400" data-testid="demo-mode-msg">{demoMsg}</div>
        )}
      </div>

      <div className="glass-card p-6 mb-5" data-testid="settings-integrations">
        <h2 className="font-display text-xl text-white mb-4">Integrations</h2>
        <p className="text-zinc-500 text-sm mb-4">Connect your inbox and CRM to enable My Leads Convert's full power.</p>
        <div className="space-y-3">
          {[
            { name: "Gmail / Outlook", status: "Not connected" },
            { name: "LinkedIn", status: "Not connected" },
            { name: "HubSpot / Salesforce", status: "Not connected" },
          ].map((i) => (
            <div key={i.name} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5">
              <div className="text-sm text-white">{i.name}</div>
              <button className="text-xs text-gold hover:underline">Connect</button>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="glass-card p-6 mb-5 border-emerald-500/30 bg-emerald-500/[0.03]" data-testid="settings-activation">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-emerald-300" />
            <h2 className="font-display text-xl text-white">Admin · Activation funnel</h2>
          </div>
          <p className="text-sm text-zinc-400 mb-5">
            How many users have reached their first real ProPublica lead — measured via the first-mine celebration event.
          </p>
          {activationLoading && <div className="text-sm text-zinc-500">Loading…</div>}
          {activationErr && <div className="text-sm text-rose-300" data-testid="activation-error">{activationErr}</div>}
          {activation && (
            <div className="space-y-4" data-testid="activation-stats">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="label-eyebrow mb-1">Total users</div>
                  <div className="font-display text-2xl text-white" data-testid="stat-total-users">{activation.total_users}</div>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="label-eyebrow mb-1">Activated</div>
                  <div className="font-display text-2xl text-emerald-300" data-testid="stat-activated">{activation.activated_users}</div>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="label-eyebrow mb-1">Activation rate</div>
                  <div className="font-display text-2xl text-white" data-testid="stat-activation-rate">{activation.activation_rate_pct}%</div>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="label-eyebrow mb-1">Median time</div>
                  <div className="font-display text-2xl text-white" data-testid="stat-median-time">{formatDuration(activation.median_seconds_to_activate)}</div>
                  {activation.p90_seconds_to_activate != null && (
                    <div className="text-[10px] text-zinc-500 mt-1">p90 {formatDuration(activation.p90_seconds_to_activate)}</div>
                  )}
                </div>
              </div>
              {activation.recent_activations && activation.recent_activations.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-2">Recent activations</div>
                  <div className="space-y-1">
                    {activation.recent_activations.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-white/[0.02] border border-white/5" data-testid={`recent-activation-${i}`}>
                        <div className="text-zinc-300 font-mono truncate">{r.email}</div>
                        <div className="text-zinc-500">{new Date(r.first_mine_at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="glass-card p-6 mb-5 border-amber-400/30 bg-amber-400/[0.03]" data-testid="settings-admin">
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="w-5 h-5 text-amber-400" />
            <h2 className="font-display text-xl text-white">Admin · Wipe user data</h2>
          </div>
          <p className="text-sm text-zinc-400 mb-5">
            Enter a user email to permanently delete ALL of their leads, campaigns, ICP, and payment records. The user account itself stays.
          </p>
          <form onSubmit={wipeUser} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              value={wipeEmail}
              onChange={(e) => setWipeEmail(e.target.value)}
              placeholder="user@example.com"
              className="input-glass flex-1"
              data-testid="admin-wipe-email"
            />
            <button type="submit" disabled={wiping} className="btn-primary" data-testid="admin-wipe-submit">
              {wiping ? <><Loader2 className="w-4 h-4 animate-spin" /> Wiping...</> : <><Trash2 className="w-4 h-4" /> Wipe data</>}
            </button>
          </form>
          {wipeResult && (
            <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm" data-testid="admin-wipe-result">
              <div className="flex items-center gap-2 text-emerald-300 font-medium mb-1">
                <Check className="w-4 h-4" /> Wiped {wipeResult.email}
              </div>
              <div className="text-zinc-300 text-xs">
                Leads: {wipeResult.deleted.leads} · Campaigns: {wipeResult.deleted.campaigns} · ICPs: {wipeResult.deleted.icps} · Payments: {wipeResult.deleted.payment_transactions}
              </div>
            </div>
          )}
          {wipeError && (
            <div className="mt-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-300" data-testid="admin-wipe-error">
              {wipeError}
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-white/5">
            <div className="text-sm font-medium text-white mb-1">Wipe all sample leads (global)</div>
            <p className="text-xs text-zinc-400 mb-4">
              Removes every non-AI-generated lead across every user in the database. Leads created by "Mine new leads" (AI-generated) are preserved.
            </p>
            <button
              type="button"
              onClick={wipeSampleLeads}
              disabled={wipingSamples}
              className="btn-ghost text-sm"
              data-testid="admin-wipe-sample-leads"
            >
              {wipingSamples ? <><Loader2 className="w-4 h-4 animate-spin" /> Wiping sample leads...</> : <><Trash2 className="w-4 h-4" /> Wipe all sample leads</>}
            </button>
            {samplesResult && (
              <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-300" data-testid="admin-wipe-samples-result">
                <Check className="w-4 h-4 inline mr-1.5" />
                Removed {samplesResult.deleted_leads} sample lead{samplesResult.deleted_leads === 1 ? "" : "s"} across all users.
              </div>
            )}
            {samplesError && (
              <div className="mt-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-300" data-testid="admin-wipe-samples-error">
                {samplesError}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="glass-card p-6 border-rose-500/20" data-testid="settings-danger">
        <h2 className="font-display text-xl text-white mb-2">Danger zone</h2>
        <p className="text-sm text-zinc-500 mb-4">Permanently delete your workspace and all leads.</p>
        <button className="px-4 py-2 rounded-full text-xs border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors">
          Delete workspace
        </button>
      </div>
    </div>
  );
}
